import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { LuMapPin } from 'react-icons/lu';
import * as Yup from 'yup';

import { TaxResidencyV2 } from '@ndvr/portfolio-api';

import { FormInput } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { convertServerDateToFormDate } from 'utils/date-formatter';

interface EditTaxResidencyDialogProps {
    residency?: TaxResidencyV2;
    onSave: (data: FormValues) => void;
    onCancel: () => void;
    open: boolean;
    isActive?: boolean;
}

const formSchema = Yup.object({
    title: Yup.string()
        .required('Title is required')
        .trim()
        .min(1, 'Title cannot be empty')
        .max(64, 'Title cannot exceed 64 characters'),
    postalCode: Yup.string()
        .required('Postal code is required')
        .trim()
        .matches(/^\d{5}(-\d{4})?$/, 'Postal code must be in format 12345 or 12345-6789'),
    effectiveFrom: Yup.string().required('Effective date is required'),
});

export type FormValues = Yup.InferType<typeof formSchema>;

export function EditTaxResidencyDialog({ residency, onSave, onCancel, open, isActive }: EditTaxResidencyDialogProps) {
    const titleInputRef = useRef<HTMLInputElement | null>(null);

    const getDefaultValues = useCallback((residencyData?: TaxResidencyV2): FormValues => {
        if (residencyData) {
            return {
                title: residencyData.title.plainText,
                postalCode: residencyData.postalCode,
                effectiveFrom: residencyData.effectiveFrom
                    ? convertServerDateToFormDate(residencyData.effectiveFrom)
                    : '',
            };
        }

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];

        return {
            title: '',
            postalCode: '',
            effectiveFrom: tomorrowStr,
        };
    }, []);

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(residency),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(residency));
    }, [residency, form, getDefaultValues]);

    useEffect(() => {
        const timer = setTimeout(() => {
            titleInputRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleSave = (data: FormValues) => {
        onSave(data);
    };

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isAdding = !residency;
    const modalTitle = isAdding ? 'Add Tax Residency' : 'Edit Tax Residency';

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LuMapPin className="h-5 w-5 text-gray-600" />
                        {modalTitle}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 px-1">
                    <Form {...form}>
                        <form id="tax-residency-form" onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                            <FormInput
                                control={form.control}
                                name="title"
                                label="Title"
                                placeholder="Enter residency title"
                                inputRef={titleInputRef}
                            />

                            <FormInput
                                control={form.control}
                                name="postalCode"
                                label="Postal Code"
                                placeholder="Enter postal code (e.g., 12345)"
                            />

                            <FormInput
                                control={form.control}
                                name="effectiveFrom"
                                label="Effective Date"
                                type="date"
                                disabled={isActive}
                            />
                        </form>
                    </Form>
                </div>
                <DialogFooter>
                    <Button variant="outline" type="button" onClick={onCancel} onMouseDown={(e) => e.preventDefault()}>
                        Cancel
                    </Button>
                    <Button variant="default" type="submit" form="tax-residency-form">
                        {isAdding ? 'Add Residency' : 'Save Changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
